import { c as _c } from "react/compiler-runtime";
import '../styles/globals.css';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/dropzone/styles.css';
import { MantineProvider } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { IconContext } from 'react-icons';
import { Notifications } from '@mantine/notifications';
import { MarkdownContext, ContextForShared, ContextInShared, SErrorBoundary } from 'shared/ui';
import { markdownProps } from 'components/MarkdownOptions';
import Head from 'next/head';
import { useEffect } from 'react';
import { AppProps } from 'next/app';
import { LicenseManager } from '@ag-grid-enterprise/core';
import { appName, theme } from 'components/theme/theme';
import React from 'react';
import { Inter, Open_Sans, Fira_Code } from 'next/font/google';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { env } from 'utils/env';
import { useRouter } from 'next/router';
import { entries } from 'remeda';
import { useVersionCheck } from 'utils/useVersion';
import { Crisp } from 'crisp-sdk-web';
const posthogKey = typeof window !== 'undefined' ? env.NEXT_PUBLIC_POSTHOG_KEY : undefined;
const shouldRun = !!posthogKey && process.env.NODE_ENV === 'production';
if (shouldRun) {
  posthog.init(posthogKey, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST ?? 'https://us.i.posthog.com',
    person_profiles: 'identified_only',
    // or 'always' to create profiles for anonymous users as well
    disable_web_experiments: true,
    advanced_disable_feature_flags: true,
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
        email: true,
        tel: true
      },
      maskTextSelector: '.sensitive'
    }
  });
}
const fontFam = Inter({
  subsets: ['latin'],
  variable: '--font-family',
  display: 'block'
});
const fontNamNumbers = Open_Sans({
  subsets: ['latin'],
  variable: '--font-family-numbers',
  display: 'block'
});
const fontFamCode = Fira_Code({
  subsets: ['latin'],
  variable: '--font-family-code',
  display: 'block'
});
const fontMd = {
  '--font-family': fontFam,
  '--font-family-numbers': fontNamNumbers,
  '--font-family-code': fontFamCode
};

// suppress useLayoutEffect (and its warnings) when not running in a browser https://gist.github.com/gaearon/e7d97cdf38a2907924ea12e4ebdf3c85?permalink_comment_id=4141024#gistcomment-4141024
if (typeof window === 'undefined') React.useLayoutEffect = () => {};
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 5 * 60 * 1000,
      staleTime: 5 * 60 * 1000,
      refetchOnMount: true
    }
  }
});
LicenseManager.setLicenseKey('Using_this_AG_Grid_Enterprise_key_( AG-047765 )_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_( legal@ag-grid.com )___For_help_with_changing_this_key_please_contact_( info@ag-grid.com )___( Swift )_is_granted_a_( Single Application )_Developer_License_for_the_application_( Swift )_only_for_( 1 )_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_( Swift )_need_to_be_licensed___( Swift )_has_been_granted_a_Deployment_License_Add-on_for_( 1 )_Production_Environment___This_key_works_with_AG_Grid_Enterprise_versions_released_before_( 13 September 2024 )____[v2]_MTcyNjE4MjAwMDAwMA==a054ce6c3860088ebd03b0c35b2ebd82');
if (env.NEXT_PUBLIC_CRISP_WEBSITE_ID && typeof window !== 'undefined') {
  Crisp.configure(env.NEXT_PUBLIC_CRISP_WEBSITE_ID, {
    autoload: false
  });
}
export default function MyApp(t0) {
  const $ = _c(15);
  const {
    Component,
    pageProps
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryClient
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useVersionCheck(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  useEffect(_temp2, t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  useEffect(_temp3, t3);
  const router = useRouter();
  let t4;
  if ($[3] !== router.events) {
    t4 = () => {
      const handleRouteChange = _temp4;
      router.events.on("routeChangeComplete", handleRouteChange);
      return () => {
        router.events.off("routeChangeComplete", handleRouteChange);
      };
    };
    $[3] = router.events;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      theme
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ReactQueryDevtools buttonPosition="bottom-left" client={queryClient} />;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = markdownProps();
    t9 = <Notifications />;
    $[8] = t8;
    $[9] = t9;
  } else {
    t8 = $[8];
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      className: "react-icons"
    };
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Head><meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" /><title>{appName}</title></Head>;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] !== Component || $[13] !== pageProps) {
    t12 = <ContextForShared mantine={t6}><MantineProvider theme={theme} forceColorScheme="light"><ContextInShared><QueryClientProvider client={queryClient}><PostHogProvider client={posthog}>{t7}<MarkdownContext.Provider value={t8}>{t9}<ModalsProvider><IconContext.Provider value={t10}><SErrorBoundary>{t11}<main><Component {...pageProps} /></main></SErrorBoundary></IconContext.Provider></ModalsProvider></MarkdownContext.Provider></PostHogProvider></QueryClientProvider></ContextInShared></MantineProvider></ContextForShared>;
    $[12] = Component;
    $[13] = pageProps;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  return t12;
}
function _temp4() {
  return posthog?.capture("$pageview");
}
function _temp3() {
  console.log("React version:", React.version);
}
function _temp2() {
  entries(fontMd).forEach(_temp);
}
function _temp(t0) {
  const [n, f] = t0;
  document.documentElement.style.setProperty(n, f.style.fontFamily);
}